import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import DocStatusPill from "./DocStatusPill";

// Matches Diamond-PO-Prototype-v3.html's docTable()/docRow() exactly (lines 783-796). One
// reusable list for My Memos / Purchase Orders (retailer) and Requests / Memos Out / Purchase
// Orders (supplier) -- parametrized by kind/status/title/who, same as the prototype's own
// phead()-wrapped screens all being thin wrappers around docTable().

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString()}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");
const dayDiff = (iso) => Math.max(1, Math.ceil((Date.now() - new Date(iso).getTime()) / 86400000));

export const DocList = ({ title, description, kind, status, whoColumn, whoField }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dealerId) return;
    setLoading(true);
    PurchaseOrderService.GetDiamondDocList({ DealerID: String(dealerId), Kind: kind || null, Status: status || null, Page: 1 })
      .then((response) => {
        setLoading(false);
        if (response.data.status === true) {
          setDocs(response.data.responseData || []);
        }
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId, kind, status]);

  return (
    <div style={{ padding: 20 }}>
      <Spin spinning={loading}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <p style={{ color: "#888", margin: 0, marginBottom: 16 }}>{description}</p>

        {docs.length === 0 ? (
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>▭</div>
            {t("Nothing here yet.")}
          </div>
        ) : (
          <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #eee", color: "#6b7689", fontSize: 11, textTransform: "uppercase" }}>
                  <th style={{ padding: "10px 12px" }}>{t("Number")}</th>
                  <th style={{ padding: "10px 12px" }}>{whoColumn}</th>
                  <th style={{ padding: "10px 12px" }}>{t("Status")}</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>{t("Stones")}</th>
                  <th style={{ padding: "10px 12px" }}>{t("Shipments")}</th>
                  <th style={{ padding: "10px 12px" }}>{t("Next due")}</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>{t("Value")}</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr
                    key={d.DiamondDocID}
                    style={{ borderBottom: "1px solid #f3f3f3", cursor: "pointer" }}
                    onClick={() => navigate(`/document/${d.DiamondDocID}`)}
                  >
                    <td style={{ padding: "10px 12px" }}>
                      <b>{d.DocNumber}</b>
                      {d.Source === "MEMO" ? <div style={{ fontSize: 11, color: "#888" }}>{t("from memo")}</div> : d.ReferenceNo ? <div style={{ fontSize: 11, color: "#888" }}>{t("Ref")} {d.ReferenceNo}</div> : null}
                    </td>
                    <td style={{ padding: "10px 12px" }}>{d[whoField]}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <DocStatusPill status={d.StatusCode} />
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{d.LineCount}</td>
                    <td style={{ padding: "10px 12px" }}>
                      {d.ShipOutCount} {t("out")} · {d.ShipReturnCount} {t("back")}
                      {d.InTransitCount > 0 && (
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, background: "#e8effc", color: "#2a52b8" }}>
                          {d.InTransitCount} {t("in transit")}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {d.NextDueUtc ? (
                        new Date(d.NextDueUtc) < new Date() ? (
                          <span style={{ color: "#c0392b", fontWeight: 600 }}>
                            {dayDiff(d.NextDueUtc)}
                            {t("d overdue")}
                          </span>
                        ) : (
                          fmtDate(d.NextDueUtc)
                        )
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>{fmtMoney((d.LineValue || 0) + (d.ChargeValue || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default DocList;
